import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabase";
import { extractMentions } from "../utils/mentions";

// Matches a whole @mention or #hashtag token in one pass — kept in sync with
// MENTION_REGEX in utils/mentions.js and the pattern in utils/hashtags.js.
const TOKEN_REGEX = /((?<![a-zA-Z0-9_])@[a-zA-Z0-9_.]+|#[a-zA-Z0-9_]+)/g;
const HASHTAG_TOKEN = /^#[a-zA-Z0-9_]+$/;

// A mention can't actually end on a "." (see utils/mentions.js) — trims a
// trailing run of dots (sentence punctuation, e.g. "thanks @amanpreet_k.")
// off before checking the 3-20 length real usernames are bound by. Returns
// the trimmed body (still original-case, for display) or null if what's
// left isn't a plausible username at all.
function mentionBody(part) {
  const body = part.slice(1).replace(/\.+$/, "");
  return body.length >= 3 && body.length <= 20 ? body : null;
}

// Renders plain text with @mentions linked to profiles and #hashtags linked
// to that tag's page — used for both captions and comments, so a hashtag
// typed anywhere in the app gets the same clickable pill treatment.
// Usernames are looked up in one small batched query per distinct piece of
// text (a caption or a comment), same idea as resolving avatar_url alongside
// a design; hashtags need no lookup since the tag itself is the destination.
export default function MentionText({ text }) {
  const [userIds, setUserIds] = useState({}); // lowercase username -> id

  useEffect(() => {
    const usernames = extractMentions(text);
    if (usernames.length === 0) return;

    let cancelled = false;

    supabase
      .from("users")
      .select("id, username")
      .in("username", usernames)
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;

        const map = {};
        data.forEach(u => {
          map[u.username.toLowerCase()] = u.id;
        });
        setUserIds(map);
      });

    return () => {
      cancelled = true;
    };
  }, [text]);

  if (!text) return null;

  const parts = text.split(TOKEN_REGEX);

  const nodes = parts.map((part, i) => {
    if (!part) return null;

    if (HASHTAG_TOKEN.test(part)) {
      return (
        <Link
          key={i}
          to={`/tag/${encodeURIComponent(part.slice(1).toLowerCase())}`}
          style={hashtagStyle}
          onClick={e => e.stopPropagation()}
        >
          {part}
        </Link>
      );
    }

    if (part.startsWith("@")) {
      const body = mentionBody(part);
      if (body) {
        const userId = userIds[body.toLowerCase()];
        if (userId) {
          // trailing is whatever mentionBody trimmed off (e.g. the "." that
          // ended the sentence) — kept as plain text right after the link
          // instead of being swallowed into the pill.
          const trailing = part.slice(1 + body.length);
          return (
            <span key={i}>
              <Link to={`/u/${userId}`} style={mentionStyle} onClick={e => e.stopPropagation()}>
                @{body}
              </Link>
              {trailing}
            </span>
          );
        }
      }
    }

    return part;
  });

  return <>{nodes}</>;
}

const mentionStyle = {
  color: "var(--accent-primary)",
  fontWeight: 700,
  textDecoration: "none",
};

const hashtagStyle = {
  display: "inline-block",
  fontSize: "0.92em",
  color: "var(--accent-contrast)",
  fontWeight: 600,
  background: "var(--accent-primary)",
  padding: "1px 8px",
  borderRadius: 10,
  textDecoration: "none",
};
